import type { ModalNavOnChangeHandler } from '@core/components'
import { ModalBody, ModalFooter, ModalHeader, ModalNav, ModalRight } from '@core/components'
import type {
  AlgorithmTypes,
  Calculation,
  FormDataML,
  MLModalNavItem,
  MLModalNavItemType,
} from '@core/core/types'
import { IconGeoML2 } from '@core/icons'
import { usingMovingModal } from '@library/model'
import { addMlErrorNotification } from '@notifications/actions'
import type { ProjectId } from 'common/types'
import { forwardRef, useCallback, useState } from 'react'

import { defaultFormValue, mlGroups } from './constants'
import { Footer } from './Footer'
import { executeCalculations, getDefaultFormData, getFullMenuItems, validateForm } from './helpers'
import css from './MachineLearningModal.css'
import { RightMenu } from './RightMenu'

type MachineLearningModalProps = {
  projectId: ProjectId
  closeModal: VoidFunction
  onClose?: VoidFunction
}

const MachineLearning = forwardRef<HTMLDivElement, MachineLearningModalProps>(
  function MachineLearning({ projectId, closeModal, onClose }, ref) {
    const [menuStep, setMenuStep] = useState<MLModalNavItemType>('algorithm')
    const [formData, setFormData] = useState<FormDataML>(defaultFormValue)

    const handleOnMenuChange = useCallback<ModalNavOnChangeHandler<MLModalNavItem>>(item => {
      setMenuStep(item.id)
    }, [])

    const mlItems = getFullMenuItems(formData.type)

    const handleFormUpdate = (formData: FormDataML) => {
      setFormData(formData)
    }
    const handleTypeChange = (type: AlgorithmTypes) => {
      if (formData.type !== type) {
        setFormData(getDefaultFormData(type))
      }
    }

    const handleClose = () => {
      closeModal()
      onClose?.()
    }

    const handleConfirm = async () => {
      handleClose()
      if (!formData.calculations[0].params) return

      const seismicSurvey = formData.calculations[0].params.seismicSurvey
      const calculations: Calculation[] = formData.calculations.map(item =>
        item.params?.seismicSurvey.entity
          ? item
          : { ...item, params: { ...item.params, seismicSurvey } },
      )
      const data: FormDataML = { ...formData, calculations }

      try {
        await executeCalculations(data, projectId)
      } catch (err: unknown) {
        addMlErrorNotification(err)
      }
    }

    const handleSave = useCallback(() => {
      console.log('Not implemented yet')
    }, [])

    const canConfirm = validateForm(formData)

    return (
      <>
        <ModalHeader
          ref={ref}
          title="Машинное обучение"
          icon={IconGeoML2}
          onCloseClick={handleClose}
        />
        <ModalBody>
          <ModalNav items={mlItems} groups={mlGroups} onChoose={handleOnMenuChange} />
          <ModalRight>
            <RightMenu
              item={menuStep}
              formData={formData}
              handleChange={handleFormUpdate}
              handleTypeChange={handleTypeChange}
            />
          </ModalRight>
        </ModalBody>
        <ModalFooter className={css.footer}>
          <Footer canConfirm={canConfirm} onConfirm={handleConfirm} onSave={handleSave} />
        </ModalFooter>
      </>
    )
  },
)

export const MachineLearningModal = usingMovingModal(MachineLearning)
